import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import BODService from "@plannel/services/data/bod-service";
import locationService from "@plannel/services/data/location-service"
import CycleCalendarRenderer from "@plannel/components/aggrid/CycleCalendarRenderer";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridAutoComplete from "@plannel/components/GridAutoComplete";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const { snackBarMessageColor } = ColorUtil;
const locSearchKey = (loc) => (loc ? `${loc.locCd ?? ""}:${loc.id ?? ""}` : "");

const BODMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.page : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const totalItemCount = useRef(0);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? { originLocation: null, destLocation: null });
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [BOD, setBOD] = useState([]);
  const [locations, setLocations] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueFormatter = (param) => {
    if (param.column?.colDef) {
      const { colId } = param.column.getColDef();
      if (colId === "originLocationCd" || colId === "destLocationCd") {
        return param.value?.locCd ? param.value.locCd : "";
      } else if (colId === "originLocationName" || colId === "destLocationName") {
        return param.value?.name ? param.value.name : "";
      }
    }

    return param.value?.name ? param.value.name : "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const suppressKeyEnter = (params) => {
    const key = params.event.key;
    return key === "Enter";
  }

  const columnDefs = [
    {
      headerName: "originLocation", groupId: "originLocation",
      children: [
        {
          headerName: "locCd", field: "originLocation", colId: "originLocationCd",
          type: ['nonEditableColumn'],
          valueFormatter,
          cellClass: 'stringType',
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
          width: 200, filterType: 'string'
        },
        {
          headerName: "locName", field: "originLocation", colId: "originLocationName",
          cellEditor: GridAutoComplete,
          cellEditorPopup: true,
          type: ["defaultValueParser"],
          valueFormatter,
          cellEditorParams: {
            valueFormatter,
            values: locations,
            equalProperty: "locCd"
          },
          suppressKeyboardEvent: suppressKeyEnter,
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
          width: 200, 
          filterType: 'string',
        }
      ]
    },
    {
      headerName: "destLocation", groupId: "destLocation",
      children: [
        {
          headerName: "locCd", field: "destLocation", colId: "destLocationCd",
          type: ['nonEditableColumn'],
          valueFormatter,
          cellClass: 'stringType',
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
          width: 200, filterType: 'string'
        },
        {
          headerName: "locName", field: "destLocation", colId: "destLocationName",
          cellEditor: GridAutoComplete,
          cellEditorPopup: true,
          type: ["defaultValueParser"],
          valueFormatter,
          cellEditorParams: {
            valueFormatter,
            values: locations,
            equalProperty: "locCd"
          },
          suppressKeyboardEvent: suppressKeyEnter,
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
          width: 200, 
          filterType: 'string',
        }
      ]
    },
    { headerName: "avgLtDays", field: "avgLtDays", type: ["numberColumn", "rightAligned"], width: 110, hide: true, suppressColumnsToolPanel: true },
    { headerName: "originPriority", field: "originPriority", type: ["numberColumn", "rightAligned"], width: 125, filterType: 'number', hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "orderCalendar", field: "cycleCalendar", type: "nonEditableColumn",
      valueGetter: params => {
        const { data } = params;
        const value = data?.cycleCalendar;
        return value?.name ? value.name : t("notConfigured");
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      cellRenderer: CycleCalendarRenderer, cellRendererParams: { setSnackInfo },
      width: 140, filterType: 'string'
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch?.originLocation) {
      searchFilters.originLocCd = locationSearch.originLocation.locCd;
    }

    if (locationSearch?.destLocation) {
      searchFilters.destLocCd = locationSearch.destLocation.locCd;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveBOD = () => {
    const params = getRequestParams();
    BODService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setBOD(results);
        setTotalPages(totalPages);
        totalItemCount.current = totalItems;
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { page: currentPage, pageSize, locationSearch, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        setLocations(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const onGridReady = params => {
    retrieveLocation();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeBOD = () => {
    const deleteIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        deleteIds.push(data.id);
      }
    );

    if (deleteIds.length > 0) {
      BODService.remove(deleteIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          if (!isEqual(totalItemCount.current, deleteIds.length)) {
            retrieveBOD();
          }
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveBOD = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      for (const data of allStateData) {
        const { destLocation, originLocation } = data;
        if (isEqual(destLocation.id, originLocation.id)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_OriginDestNotSame") });
          return;
        }
      }

      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let activeFlgChanged = false;
        if (originalData) {
          activeFlgChanged = originalData.activeFlg !== row.activeFlg;
        }

        return { ...row, activeFlgChanged };
      });  

      BODService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .finally(() => {
          retrieveBOD();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveBOD();
    } else if (action === "DELETE") {
      removeBOD();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveBOD();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      originLocation: null,
      destLocation: null,
      activeFlg: true,
      avgLtDays: 0,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
    
    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();
    const hasActiveFlgChanges = checkActiveFlgChanges();
    
    if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.bod, 'deactivate')}` });
    } else {  
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };

  useEffect(() => {
    if (gridLoading) {
      retrieveBOD();
    }

    return () => {
      setBOD([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setBOD([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locSearchKey(locationSearch.originLocation), locSearchKey(locationSearch.destLocation),
      advancedFilters]);

  // BOD 화면이 열린 상태에서 검색 조건을 설정하여 BOD 화면으로의 화면 이동이 발생하는 경우
  useEffect(() => {
    const onLinkSearch = (e) => {
      const vs = e?.detail || {};
      if (vs.locationSearch) setLocationSearch(vs.locationSearch);
      setAdvancedFilters(vs.advancedFilters ?? null);
      setAdvancedFilterColor(!isEmpty(vs.advancedFilters) ? "info" : "default");
    };
    window.addEventListener("plannel:bodSearch", onLinkSearch);
    return () => window.removeEventListener("plannel:bodSearch", onLinkSearch);
  }, []);

  useEffect(() => {
    if (!isEmpty(locations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);
  
  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.originLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, originLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, mr: 1 }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("originLocation")}
            />
          )}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.destLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, destLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ mr: 1, minWidth: 240 }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("destLocation")}
            />
          )}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={BODService} params={getRequestParams()}
            columnDefs={columnDefs.filter(col => !col.hide)} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.bod, 'delete')}` })} />
          <SaveButton onClick={handleSaveClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="bod-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={BOD}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(BODMaster);
